import type { Widget } from "@emkit/builder-core";
import {
  ACCORDION_DEFAULTS,
  ALERT_DEFAULTS,
  AUDIO_DEFAULTS,
  BADGE_DEFAULTS,
  BREADCRUMBS_DEFAULTS,
  BUTTON_DEFAULTS,
  CAROUSEL_DEFAULTS,
  CHART_DEFAULTS,
  CODE_DEFAULTS,
  COUNTDOWN_DEFAULTS,
  CTA_BANNER_DEFAULTS,
  DIVIDER_DEFAULTS,
  FAQ_DEFAULTS,
  FEATURES_DEFAULTS,
  FORM_DEFAULTS,
  GALLERY_DEFAULTS,
  HEADING_DEFAULTS,
  HERO_DEFAULTS,
  HTML_DEFAULTS,
  ICON_LIST_DEFAULTS,
  IMAGE_DEFAULTS,
  LOGO_BAR_DEFAULTS,
  MAP_DEFAULTS,
  NAV_MENU_DEFAULTS,
  NEWSLETTER_DEFAULTS,
  PRICING_DEFAULTS,
  PRODUCT_CARD_DEFAULTS,
  PROGRESS_DEFAULTS,
  QUOTE_DEFAULTS,
  SHARE_DEFAULTS,
  SOCIAL_LINKS_DEFAULTS,
  SPACER_DEFAULTS,
  STATS_DEFAULTS,
  STEPS_DEFAULTS,
  TABS_DEFAULTS,
  TEAM_DEFAULTS,
  TESTIMONIALS_DEFAULTS,
  TEXT_DEFAULTS,
  TIMELINE_DEFAULTS,
  VIDEO_DEFAULTS,
} from "@emkit/builder-widgets";

/**
 * Drag payloads. Every draggable set via dnd-kit carries one of these
 * in its `data.current`. Types make onDragEnd handlers safe.
 */
export type DragData =
  | { kind: "palette"; widgetType: string }
  | { kind: "widget"; widgetId: string; columnId: string };

const defaultsByType: Record<string, () => Record<string, unknown>> = {
  heading: () => ({ ...HEADING_DEFAULTS }),
  text: () => ({ ...TEXT_DEFAULTS }),
  image: () => ({ ...IMAGE_DEFAULTS }),
  button: () => ({ ...BUTTON_DEFAULTS }),
  spacer: () => ({ ...SPACER_DEFAULTS }),
  divider: () => ({ ...DIVIDER_DEFAULTS }),
  video: () => ({ ...VIDEO_DEFAULTS }),
  html: () => ({ ...HTML_DEFAULTS }),
  form: () => ({ ...FORM_DEFAULTS }),
  // Deep-clone items so each instance gets its own mutable list.
  accordion: () => ({ ...structuredClone(ACCORDION_DEFAULTS) }),
  tabs: () => ({ ...structuredClone(TABS_DEFAULTS) }),
  gallery: () => ({ ...structuredClone(GALLERY_DEFAULTS) }),
  "icon-list": () => ({ ...structuredClone(ICON_LIST_DEFAULTS) }),
  hero: () => ({ ...structuredClone(HERO_DEFAULTS) }),
  pricing: () => ({ ...structuredClone(PRICING_DEFAULTS) }),
  testimonials: () => ({ ...structuredClone(TESTIMONIALS_DEFAULTS) }),
  stats: () => ({ ...structuredClone(STATS_DEFAULTS) }),
  features: () => ({ ...structuredClone(FEATURES_DEFAULTS) }),
  "logo-bar": () => ({ ...structuredClone(LOGO_BAR_DEFAULTS) }),
  "cta-banner": () => ({ ...structuredClone(CTA_BANNER_DEFAULTS) }),
  faq: () => ({ ...structuredClone(FAQ_DEFAULTS) }),
  alert: () => ({ ...ALERT_DEFAULTS }),
  quote: () => ({ ...QUOTE_DEFAULTS }),
  "social-links": () => ({ ...structuredClone(SOCIAL_LINKS_DEFAULTS) }),
  progress: () => ({ ...PROGRESS_DEFAULTS }),
  badge: () => ({ ...BADGE_DEFAULTS }),
  code: () => ({ ...CODE_DEFAULTS }),
  map: () => ({ ...MAP_DEFAULTS }),
  countdown: () => {
    const next = structuredClone(COUNTDOWN_DEFAULTS);
    // Reset "target" to 7 days from now so each dropped widget gets a
    // fresh, in-the-future timestamp rather than the stale module-load one.
    next.target = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString();
    return { ...next };
  },
  breadcrumbs: () => ({ ...structuredClone(BREADCRUMBS_DEFAULTS) }),
  "nav-menu": () => ({ ...structuredClone(NAV_MENU_DEFAULTS) }),
  share: () => ({ ...structuredClone(SHARE_DEFAULTS) }),
  audio: () => ({ ...AUDIO_DEFAULTS }),
  chart: () => ({ ...structuredClone(CHART_DEFAULTS) }),
  team: () => ({ ...structuredClone(TEAM_DEFAULTS) }),
  steps: () => ({ ...structuredClone(STEPS_DEFAULTS) }),
  timeline: () => ({ ...structuredClone(TIMELINE_DEFAULTS) }),
  newsletter: () => ({ ...NEWSLETTER_DEFAULTS }),
  carousel: () => ({ ...structuredClone(CAROUSEL_DEFAULTS) }),
  "product-card": () => ({ ...PRODUCT_CARD_DEFAULTS }),
};

function newId(type: string): string {
  return `${type}-${Math.random().toString(36).slice(2, 8)}`;
}

export function createDefaultWidget(type: string): Widget | null {
  const make = defaultsByType[type];
  if (!make) return null;
  return {
    id: newId(type),
    type,
    props: make(),
  };
}
